import type { ErrorCode } from "@contract";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ApiRequestError } from "../../api/fetcher";
import { useUploadBook } from "../../api/hooks";
import { Button } from "../../components/control/Button";
import { FileDrop } from "../../components/control/FileDrop";
import { GateButton } from "../../components/control/GateButton";
import { Card } from "../../components/data/Card";
import { ErrorBanner } from "../../components/feedback/ErrorBanner";
import { ProgressBar } from "../../components/feedback/ProgressBar";
import { useGuidance } from "../../components/guidance";
import { bookLandingPath } from "../book/landing";
import { ACCEPT_ATTRIBUTE, sourceRejection } from "./source";

/**
 * The left half of the dashboard, per docs/mockup/Upload.dc.html: the drop
 * zone, what the formats mean, and one primary action. A PDF is refused here
 * without a request; the same file renamed to .docx goes out and comes back
 * 415, and both render the same errors:PDF_NOT_SUPPORTED sentence.
 */
export function UploadPanel() {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [rejected, setRejected] = useState<ErrorCode | null>(null);
  const { mutation, progress, cancel } = useUploadBook();
  // "Hochladen und einlesen" with no file says so and points at the drop zone
  // instead of sitting greyed out.
  const guide = useGuidance();

  const choose = (files: File[]) => {
    const chosen = files[0];
    if (!chosen) return;
    mutation.reset();
    const rejection = sourceRejection(chosen);
    setRejected(rejection);
    setFile(rejection ? null : chosen);
  };

  const send = () => {
    if (!file) return;
    mutation.mutate(file, {
      // Where the new Book's own state says it belongs, which for a Book
      // whose Parse has just started is the Book page: /chapters has nothing
      // to show and its nav step is locked until the Manuscript exists.
      // A cancelled upload resolves to null and stays on the dashboard.
      onSuccess: (book) => {
        if (book) void navigate(bookLandingPath(book.id, book.state));
      },
    });
  };

  // One error line: what the browser refused, or what the service answered.
  const error = rejected ? new ApiRequestError(rejected, 0) : mutation.error;

  return (
    <Card title={t("upload.title")} className="min-h-0">
      <div className="flex flex-1 flex-col gap-5">
        <FileDrop
          accept={ACCEPT_ATTRIBUTE}
          onFiles={choose}
          onClear={() => {
            setFile(null);
            setRejected(null);
            mutation.reset();
          }}
          disabled={mutation.isPending}
          hint={t("upload.dropHint")}
          file={file}
          error={guide.attempted && !file ? t("upload.noFile") : undefined}
        />
        <p className="text-ink-2">{t("upload.formats")}</p>
        <p className="text-ink-2">{t("upload.nextStep")}</p>
        {error ? <ErrorBanner error={error} /> : null}
        {mutation.isPending ? (
          <div className="flex flex-col gap-2">
            <ProgressBar
              value={progress ?? 0}
              label={t("upload.progressLabel")}
              showPercent={progress !== null}
            />
            <p className="font-mono text-data text-ink-2">{t("upload.submitting")}</p>
          </div>
        ) : null}
        <div className="flex-1" />
        <div className="flex justify-end gap-3">
          {/* The one control that stays live while the body goes out: without
              it a stalled upload leaves a page reload as the only way out. */}
          {mutation.isPending ? (
            <Button variant="secondary" onClick={cancel}>
              {t("upload.cancel")}
            </Button>
          ) : null}
          <GateButton
            gate={{ blocked: !file, reason: t("upload.noFile") }}
            onBlocked={guide.proceed}
            onClick={send}
            busy={mutation.isPending}
          >
            {t("upload.submit")}
          </GateButton>
        </div>
      </div>
    </Card>
  );
}
